/**
 * Zod schemas for WebSocket wire envelopes (Plan 05).
 *
 * ServerMessage — every message the hub pushes to a client.
 *   topic: TopicName (enum — rejects drift at parse time)
 *   event: short string (e.g. 'reload', 'pong', 'member.created' in Phase 2+)
 *   id:    UUIDv7 generated per message on the server
 *   at:    ISO-8601 datetime
 *   data:  topic/event-specific payload; kept `unknown` in Phase 1 — Phase 2+
 *          introduces per-event schemas as additive refinements.
 *
 * ClientMessage — discriminated union of everything a client can send.
 *   { kind: 'subscribe',   topics: [...] }   — add topics to this client's set
 *   { kind: 'unsubscribe', topics: [...] }   — remove (except 'system')
 *   { kind: 'ping' }                         — HIGH-5 heartbeat ping
 *
 * MED-8: the hub parses every incoming frame against ClientMessage via
 * safeParse — malformed frames are logged and dropped, not fatal.
 */
import { z } from 'zod';
import { TOPIC_NAMES } from './topics';

export const ServerMessage = z.object({
  topic: z.enum(TOPIC_NAMES),
  event: z.string().min(1),
  id: z.string().min(1),
  at: z.iso.datetime(),
  data: z.unknown(),
});
export type ServerMessage = z.infer<typeof ServerMessage>;

export const ClientMessage = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('subscribe'),
    topics: z.array(z.enum(TOPIC_NAMES)).min(1),
  }),
  z.object({
    kind: z.literal('unsubscribe'),
    topics: z.array(z.enum(TOPIC_NAMES)).min(1),
  }),
  z.object({ kind: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;
